/**
 * Upload Inactivity Middleware
 * -------------------------------------
 * This middleware uses Redux Toolkit's createListenerMiddleware to monitor upload activity state.
 * 
 * Purpose:
 * - Automatically pause the inactivity logout timer when a file upload is in progress.
 * - Automatically resume and reset the inactivity timer when all uploads are complete or aborted.
 * - Prevents users from being logged out due to inactivity while uploads are happening.
 * 
 * How it works:
 * - Watches the Redux state for changes in upload activity (IN_PROGRESS or PENDING).
 * - When upload starts: 
 *     - Pauses the inactivity timer (calls pauseInactivityChecks).
 *     - Logs "Upload in progress: inactivity timer paused. Progress: n/a".
 * - When upload ends:
 *     - Resumes and resets the inactivity timer (calls startInactivityChecks).
 *     - Logs "No upload in progress: inactivity timer resumed and reset."
 * - Only triggers when the upload-active state actually changes, avoiding unnecessary work.
 * Integration:
 * - Should be added to the Redux store middleware chain.
 * - Relies on pauseInactivityChecks and startInactivityChecks exported from inactivityMonitor.js.
 */


import { createListenerMiddleware } from '@reduxjs/toolkit';
import { UPLOAD_STATUS } from '../../constants/uploadStatus';
import { pauseInactivityChecks,startInactivityChecks } from '../../utils/inactivityMonitor';
/**
 * Selector: returns true if any upload session is currently IN_PROGRESS or PENDING.
 * @param {object} state - Redux root state
 * @returns {boolean}
 */
const selectIsUploadActive = (state) => {
    const activeUploads = state.uploadProgress?.activeUploads;
    return Array.isArray(activeUploads) && activeUploads.some(session =>
        session.status === UPLOAD_STATUS.IN_PROGRESS   
        
    );
};

const uploadInactivityMiddleware = createListenerMiddleware();

uploadInactivityMiddleware.startListening({
    predicate: (_action, currentState, previousState) => {
        return selectIsUploadActive(currentState) !== selectIsUploadActive(previousState);
    },
    effect: (_action, listenerApi) => {
        const active = selectIsUploadActive(listenerApi.getState());
        if (active) {
            pauseInactivityChecks();
        } else {

            startInactivityChecks();
        }
    },
});

export default uploadInactivityMiddleware;
